import { message, Select, Skeleton, Space } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import { useImperativeHandle, useState } from 'react';
import RoleType from '../../../consts/RoleType';
import RoleDTO from '../../../models/RoleDTO';
import Validator from '../../../models/Validator';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import Guid from '../../../utils/Guid';
import LookupsUtil from '../../../utils/LookupsUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import UserRegionalPlanningPartnerAssignmentApiService from '../../../api/UserRegionalPlanningPartnerAssignmentApiService';
import UserSearchInput from '../../general/UserSearchInput';
import Dropdown from '../../inputs/Dropdown';
import * as AddUserToRegionalPlanningPartnerHandler from '../../../handlerModels/AddUserToRegionalPlanningPartnerHandler';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';

interface AddUserToRPPFormProps extends BaseFormProps {
  rppIdentifier: number;
  userId?: string;
  submitted?: boolean;
  onSubmit?: () => void;
  resetForm?: (value: string) => void;
}

const AddUserToRPPForm = React.forwardRef((props: AddUserToRPPFormProps, ref) => {
  const _formRef = React.createRef<FormInstance>();

  const _formItems = new Map<string, FormItemProps>()
    .set(AddUserToRegionalPlanningPartnerHandler.Request.userId, {
      label: 'User',
      required: true,
      name: AddUserToRegionalPlanningPartnerHandler.Request.userId,
      rules: [
        ValidationRuleUtil.required()
      ]
    })
    .set(AddUserToRegionalPlanningPartnerHandler.Request.roleIds, {
      label: 'RPP Roles',
      required: true,
      name: AddUserToRegionalPlanningPartnerHandler.Request.roleIds,
      rules: [
        ValidationRuleUtil.required()
      ]
    });

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(),
    reset: () => reset()
  }));

  const reset = () => {
    setFieldErrors(null);
    setSubmitted(false);
    _formRef.current?.resetFields();
  }


  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(true);
  const [, setError] = useState(false);
  const [roles, setRoles] = useState([] as RoleDTO[]);
  const [fieldErrors, setFieldErrors] = useState({} as ({ [key: string]: Validator[]; } | null));

  React.useEffect(() => {
    fetchData()
  }, []);

  const fetchData = () => {
    setLoading(true);
    const loaders = [];

    if (!roles || roles.length == 0) {
      loaders.push(loadRoles());
    }

    Promise.all(loaders).then(() => {
      setLoading(false);
      if (props.resetForm) {
        props.resetForm;
      }
    });
  }

  const loadRoles = () => {
    return LookupsUtil.getAll<RoleDTO>(RoleDTO.className)
      .then((results: RoleDTO[]) => {
        if (results) {
          setRoles(results);
        }
      }).catch(() => {
        setError(true);
      });
  }

  const renderRole = (role: RoleDTO) => {
    if (role.id && role.roleTypeId == RoleType.RPP) {
      return <Select.Option key={role.id ?? Guid.Empty()} value={role.id ?? Guid.Empty()}> {role.name}</Select.Option>
    }
  }

  const handleSubmit = () => {
    setSubmitted(true);

    const model = _formRef.current?.getFieldsValue();

    const request = AddUserToRegionalPlanningPartnerHandler.Request.create({
      rppId: props.rppIdentifier,
      userId: model.userId.id,
      roleIds: model.roleIds
    });

    UserRegionalPlanningPartnerAssignmentApiService.addRPPUser(request)
      .then((result: AddUserToRegionalPlanningPartnerHandler.Result) => {
        if (result?.succeeded) {
          message.success('User Added');
          setFieldErrors(null);
          setSubmitted(false);

          if (props.onSubmit) {
            props.onSubmit();
          }
        }
        else {
          setFieldErrors(result.fieldErrors);
          message.error('Error Adding User');
        }
      })
      .catch(() => {
        message.error('Error Adding User');
      });
  }


  if (loading) {
    return <Skeleton active />
  }
  else {
    return (
      <Form
        ref={_formRef}
        layout="vertical"
        requiredMark={true}
        onFinish={handleSubmit}>
        <Space size="small" direction="vertical">
          <Form.Item
            {..._formItems.get(AddUserToRegionalPlanningPartnerHandler.Request.userId)}
            {...ValidationUtil.getValidation(AddUserToRegionalPlanningPartnerHandler.Request.userId, fieldErrors, submitted)}>
            <UserSearchInput />
          </Form.Item>

          <FormItem
            {..._formItems.get(AddUserToRegionalPlanningPartnerHandler.Request.roleIds)}
            {...ValidationUtil.getValidation(AddUserToRegionalPlanningPartnerHandler.Request.roleIds, fieldErrors, submitted)}>
            <Dropdown dropdownMatchSelectWidth={false}
              mode="multiple">
              {roles.map(x => { return renderRole(x) })}
            </Dropdown>
          </FormItem>
        </Space >
      </Form >
    );
  }

})

AddUserToRPPForm.displayName = 'AddUserToRPPForm'
export default AddUserToRPPForm;